import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQueries } from '@tanstack/react-query';

import { Select } from '@sopranodesign/pulse-components';

const GenerativeAiTagCondition = ({ onChange, value, ...props }) => {
  const scopes = [
    'generative_ai_files',
    'generative_ai_sitemap_urls',
    'generative_ai_sharepoint_files',
    'generative_ai_zendesk_articles'
  ];

  // Fetch data for each scope separately
  const queries = useQueries({
    queries: scopes.map(scope => ({
      queryKey: ['groups', { scoped_to: scope }],
      staleTime: 5 * 60 * 1000
    }))
  });

  // Check if any query is loading
  const isLoading = queries.some(q => q.isLoading);

  // Combine all tags from different scopes
  const tags = useMemo(() => {
    const allTags = [];
    queries.forEach(query => {
      if (query.data?.data) {
        allTags.push(...query.data.data);
      }
    });
    return allTags;
  }, [queries]);

  const tagOptions = useMemo(() => {
    const uniqueTagsMap = new Map();
    tags.forEach(tag => {
      // Keep the first occurrence of each tag name
      if (!uniqueTagsMap.has(tag.name)) {
        uniqueTagsMap.set(tag.name, tag.id);
      }
    });
    return Array.from(uniqueTagsMap, ([name, id]) => ({
      label: name,
      value: id
    }));
  }, [tags]);

  // Convert value (array of IDs or objects) to selected options for multi-select
  const selectedTags = useMemo(() => {
    if (!value || !Array.isArray(value)) {
      return [];
    }
    // Handle both array of IDs and array of objects {id, name}
    const ids = value.map(item => (typeof item === 'object' ? item.id : item));
    return tagOptions.filter(option => ids.includes(option.value));
  }, [value, tagOptions]);

  const handleChange = options => {
    if (options && options.length > 0) {
      // Extract IDs as integers and send as array
      onChange(options.map(opt => parseInt(opt.value, 10)));
    } else {
      // Send null for empty selection (backend accepts null)
      onChange(null);
    }
  };

  return (
    <Select
      isMulti
      isClearable
      isLoading={isLoading}
      value={selectedTags}
      onChange={handleChange}
      options={tagOptions}
      placeholder="Select generative AI tags"
      {...props}
    />
  );
};

GenerativeAiTagCondition.propTypes = {
  value: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func.isRequired
};

export default GenerativeAiTagCondition;
